import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { PushNotifications } from '@capacitor/push-notifications';
import { getPlatform, isNative } from './capacitor';
import { isLikelyApnsToken } from '../services/deviceToken';

const FCM_RETRY_MS = 2500;
const FCM_MAX_ATTEMPTS = 15;

export async function resolveFcmToken() {
  try {
    const tokenResp = await FirebaseMessaging.getToken();
    const token = tokenResp?.token || null;
    if (token && !isLikelyApnsToken(token)) return token;
    return null;
  } catch {
    return null;
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Poll for a valid FCM token (iOS often delivers after APNs).
 * @returns {Promise<string|null>}
 */
export async function resolveFcmTokenWithRetry() {
  for (let i = 0; i < FCM_MAX_ATTEMPTS; i += 1) {
    const token = await resolveFcmToken();
    if (token) return token;
    if (i < FCM_MAX_ATTEMPTS - 1) await sleep(FCM_RETRY_MS);
  }
  return null;
}

let refreshListenerAdded = false;

/**
 * @param {(token: string) => void | Promise<void>} onToken
 */
export function setupPushTokenRefreshListener(onToken) {
  if (!isNative() || refreshListenerAdded) return;
  refreshListenerAdded = true;

  PushNotifications.addListener('registration', async ({ value }) => {
    if (!value) return;
    if (getPlatform() === 'ios' && isLikelyApnsToken(value)) {
      const fcm = await resolveFcmToken();
      if (fcm) onToken(fcm);
      return;
    }
    if (!isLikelyApnsToken(value)) onToken(value);
  });

  FirebaseMessaging.addListener('tokenReceived', async (event) => {
    if (event?.token && !isLikelyApnsToken(event.token)) onToken(event.token);
  });
}

/** @returns {Promise<boolean>} */
export async function ensurePushRegistration() {
  if (!isNative()) return false;

  const { receive } = await PushNotifications.checkPermissions();
  if (receive === 'granted') {
    await PushNotifications.register();
    return true;
  }

  const requested = await PushNotifications.requestPermissions();
  if (requested.receive !== 'granted') return false;

  await PushNotifications.register();
  return true;
}

/**
 * Request permission and return the device FCM token.
 * @returns {Promise<string|null>}
 */
export async function registerAndGetToken() {
  if (!isNative()) return null;
  const granted = await ensurePushRegistration();
  if (!granted) return null;
  return resolveFcmTokenWithRetry();
}
